
import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Pitch, PitchFeedback } from '@/types/pitch';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  CalendarIcon, 
  MessageSquare, 
  ThumbsUp, 
  Send, 
  Loader2, 
  CheckCircle
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PitchDetailDialogProps {
  pitch: Pitch | null;
  feedback: PitchFeedback[];
  isLoading: boolean;
  onClose: () => void;
  onSubmitFeedback: (pitchId: string, content: string) => Promise<void>;
  onMarkHelpful: (feedbackId: string) => Promise<void>;
  onUpvote: (pitchId: string) => void;
}

const PitchDetailDialog: React.FC<PitchDetailDialogProps> = ({
  pitch,
  feedback,
  isLoading,
  onClose,
  onSubmitFeedback,
  onMarkHelpful,
  onUpvote
}) => {
  const { user } = useAuth();
  const [feedbackContent, setFeedbackContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markingHelpful, setMarkingHelpful] = useState<string | null>(null);

  const categoryColors = {
    business: 'bg-blue-100 text-blue-800',
    social_impact: 'bg-green-100 text-green-800',
    creative: 'bg-purple-100 text-purple-800',
    other: 'bg-gray-100 text-gray-800'
  };

  const handleSubmit = async () => {
    if (!pitch || !feedbackContent.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmitFeedback(pitch.id, feedbackContent);
      setFeedbackContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkHelpful = async (feedbackId: string) => {
    setMarkingHelpful(feedbackId);
    try {
      await onMarkHelpful(feedbackId);
    } finally {
      setMarkingHelpful(null);
    }
  };

  if (!pitch) return null;

  const categoryColor = categoryColors[pitch.category];
  const formattedDate = pitch.created_at ? 
    formatDistanceToNow(new Date(pitch.created_at), { addSuffix: true }) : '';
  const isPitchOwner = user && user.id === pitch.user_id;

  return (
    <Dialog open={!!pitch} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold">{pitch.title}</DialogTitle>
            <Badge className={`${categoryColor}`}>
              {pitch.category.replace('_', ' ')}
            </Badge>
          </div>
          <DialogDescription className="flex items-center gap-1 text-sm">
            <CalendarIcon className="h-3.5 w-3.5" /> {formattedDate}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Author info */}
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={pitch.author?.avatar_url || ""} />
              <AvatarFallback className="bg-primary/20">
                {pitch.author?.username?.[0] || pitch.author?.full_name?.[0] || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="font-medium">
              {pitch.author?.username || pitch.author?.full_name || "Anonymous"}
            </div>
          </div>

          {/* Description */}
          <div className="text-sm leading-relaxed mb-2 whitespace-pre-wrap">
            {pitch.description}
          </div>

          {/* Upvote button */}
          <div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onUpvote(pitch.id)}
              className={pitch.has_upvoted ? "text-primary" : ""}
            >
              <ThumbsUp className={`h-4 w-4 mr-2 ${pitch.has_upvoted ? "fill-primary" : ""}`} /> 
              {pitch.upvotes_count || 0} Upvotes
            </Button>
          </div>

          <Separator />
          
          {/* Feedback section title */}
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            <h3 className="font-semibold">Feedback & Suggestions</h3>
          </div>

          {/* Feedback input */}
          {user && (
            <div className="space-y-2">
              <Textarea
                placeholder="Share your thoughts, suggestions, or encouragement..."
                value={feedbackContent}
                onChange={(e) => setFeedbackContent(e.target.value)}
                className="min-h-[100px]"
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleSubmit}
                  disabled={!feedbackContent.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" /> Submit Feedback
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Feedback list */}
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No feedback yet. Be the first to provide feedback!
            </div>
          ) : (
            <ScrollArea className="max-h-[300px] pr-4">
              <div className="space-y-4">
                {feedback.map((item) => (
                  <div 
                    key={item.id} 
                    className={`p-4 rounded-md border ${item.is_helpful ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={item.author?.avatar_url || ""} />
                          <AvatarFallback className="text-xs">
                            {item.author?.username?.[0] || item.author?.full_name?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">
                          {item.author?.username || item.author?.full_name || "Anonymous"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                      </div>
                    </div>
                    <p className="text-sm mb-2">{item.content}</p>
                    
                    {/* Helpful badge or button */}
                    <div className="flex justify-end">
                      {item.is_helpful ? (
                        <div className="flex items-center text-green-600 text-xs gap-1">
                          <CheckCircle className="h-3.5 w-3.5" /> Marked as helpful
                        </div>
                      ) : isPitchOwner && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs"
                          onClick={() => handleMarkHelpful(item.id)}
                          disabled={!!markingHelpful}
                        >
                          {markingHelpful === item.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          )} 
                          Mark as helpful
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PitchDetailDialog;
